import React, { useState, useEffect, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { Minus, Plus, ShoppingBag } from "lucide-react";
import { API_URL, getCart, addToCart, updateCartItem } from "../utils/cartApi";

const safeImageUrl = (url) => {
  if (!url || typeof url !== "string") return "/placeholder.png";
  const blocked = /(localhost|127\.0\.0\.1|:7071)/i;
  return blocked.test(url) ? "/placeholder.png" : url;
};

const ProductDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  const [product, setProduct] = useState(null);
  const [similarProducts, setSimilarProducts] = useState([]);
  const [activeImage, setActiveImage] = useState(0);
  const [quantityInCart, setQuantityInCart] = useState(0);
  const [loading, setLoading] = useState(true);

  const quantityRef = useRef(0);

  const updateQuantityInstantly = (newQty) => {
    const safeQty = Math.max(0, newQty);
    const oldQty = quantityRef.current;
    const delta = safeQty - oldQty;

    quantityRef.current = safeQty;
    setQuantityInCart(safeQty);

    window.dispatchEvent(
      new CustomEvent("cartUpdated", {
        detail: { delta }
      })
    );

    return oldQty;
  };

  useEffect(() => {
    const fetchProduct = async () => {
      try {
        setLoading(true);
        // Ensure the URL correctly points to the /api endpoint
        const res = await axios.get(`${API_URL}/api/products/${id}`);
        console.log("DEBUG - API RESPONSE:", res.data); // To verify data is arriving
        
        const productData = res.data.product ? res.data.product : res.data;
        setProduct(productData);
        
        if (res.data.similarProducts && Array.isArray(res.data.similarProducts)) {
          setSimilarProducts(res.data.similarProducts);
        } else {
          setSimilarProducts([]);
        }
      } catch (err) {
        console.error("❌ ProductDetail fetch error:", err.response?.data || err.message);
        setProduct(null);
      } finally {
        setLoading(false);
      }
    };

    if (id) fetchProduct();
  }, [id]);

  useEffect(() => {
    const refreshCartQuantity = async () => {
      if (!product) return;

      try {
        const cart = await getCart();
        const item = cart.items?.find(
          (entry) => String(entry.productId) === String(product._id)
        );

        const qty = item?.quantity || 0;
        quantityRef.current = qty;
        setQuantityInCart(qty);
      } catch (err) {
        console.error(err);
      }
    };

    refreshCartQuantity();
  }, [product]);

  const handleAddToCart = async () => {
    if (!product) return;
    if (Number(product.stock) <= 0) {
      alert("This product is out of stock.");
      return;
    }
    const previousQty = updateQuantityInstantly(1);

    try {
      await addToCart(
        {
          _id: product._id,
          sareeId: product.sareeId || "",
          name: product.name,
          price: product.price,
          stock: product.stock,
          image: safeImageUrl(product.images?.[0] || product.image || "/placeholder.png"),
        },
        1
      );
    } catch (err) {
      console.error(err);
      updateQuantityInstantly(previousQty);
    }
  };

  const handleQuantityChange = async (newQty) => {
    if (!product) return;

    const safeQty = Math.max(0, newQty);
    const previousQty = updateQuantityInstantly(safeQty);

    try {
      await updateCartItem(product._id, safeQty);
    } catch (err) {
      console.error(err);
      updateQuantityInstantly(previousQty);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-cover bg-repeat flex items-center justify-center" style={{ backgroundImage: "url('/bg-parchment.jpg')" }}>
        <p className="text-[#3d0c11] font-serif text-lg">Loading product...</p>
      </div>
    );
  }

  if (!product) {
    return (
      <div className="min-h-screen bg-cover bg-repeat flex items-center justify-center" style={{ backgroundImage: "url('/bg-parchment.jpg')" }}>
        <div className="text-center">
          <ShoppingBag size={64} className="mx-auto text-[#3d0c11] opacity-20 mb-4" />
          <p className="text-[#3d0c11] font-serif text-lg">Product not found</p>
          <button onClick={() => navigate("/explore")} className="mt-4 text-blue-600">
            Return to Explore
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-cover bg-repeat py-10" style={{ backgroundImage: "url('/bg-parchment.jpg')" }}>
      <div className="max-w-5xl mx-auto px-4">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-10">
          <div>
            <div className="bg-white mb-4 aspect-[3/4] overflow-hidden">
              <img
                src={safeImageUrl(product.images?.[activeImage] || product.image)}
                alt={product.name}
                className="w-full h-full object-cover"
              />
            </div>

            <div className="flex gap-2">
              {product.images?.map((img, idx) => (
                <div
                  key={`${product._id}-${idx}`}
                  onClick={() => setActiveImage(idx)}
                  className={`w-16 h-20 cursor-pointer border-2 overflow-hidden ${
                    activeImage === idx ? "border-[#3d0c11]" : "border-gray-200"
                  }`}
                >
                  <img src={safeImageUrl(img)} alt="" className="w-full h-full object-cover" />
                </div>
              ))}
            </div>
            {similarProducts.length > 0 && (
              <div className="mt-6">
                <h3 className="text-[#3d0c11] font-semibold mb-3">Available Colours</h3>
                <div className="flex gap-3 flex-wrap">
                  {similarProducts.map(item => (
                    <div
                      key={item._id}
                      onClick={() => navigate(`/product/${item._id}`)}
                      className="cursor-pointer border-2 border-gray-200 hover:border-[#3d0c11] p-1"
                    >
                      <img
                        src={safeImageUrl(item.images?.[0])}
                        alt={item.color}
                        className="w-16 h-20 object-cover"
                      />
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          <div className="bg-white p-6">
            <h1 className="text-[#3d0c11] font-serif text-3xl mb-2 uppercase">
              {product.name}
            </h1>

            <p className="text-[#8c7b70] text-lg font-serif italic mb-4">
              ₹ {Number(product.price).toLocaleString()}
            </p>

            <p className="text-gray-600 mb-6 leading-relaxed">
              {product.description}
            </p>

            {quantityInCart === 0 ? (
              <button
                onClick={handleAddToCart}
                className="w-full bg-[#3d0c11] text-white py-4 font-bold uppercase text-[10px] tracking-widest hover:bg-black transition"
              >
                <ShoppingBag size={16} className="inline mr-2" />
                Add to Cart
              </button>
            ) : (
              <div className="w-full flex items-center justify-between bg-[#3d0c11] text-white">
                <button
                  onClick={() => handleQuantityChange(quantityInCart - 1)}
                  className="w-16 py-4 flex justify-center hover:bg-black"
                >
                  <Minus size={18} />
                </button>

                <span className="font-bold text-sm tracking-widest">
                  {quantityInCart}
                </span>

                <button
                  onClick={() => {if (quantityInCart >= Number(product.stock)) {
                    alert(`Only ${product.stock} pieces available in stock`);
                    return;
                  }
                  handleQuantityChange(quantityInCart + 1);
                }}
                  className="w-16 py-4 flex justify-center hover:bg-black"
                >
                  <Plus size={18} />
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProductDetail;